import React, { useState } from 'react';
import VideoUploadButton from '../../../components/ui/VideoUploadButton';
import Icon from '../../../components/AppIcon';

const LevelFiveSection = ({ data, onChange, errors, songId, talentId }) => {
  const [uploadedVideos, setUploadedVideos] = useState([]);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  const handleVideoUploadSuccess = (uploadData) => {
    setUploadedVideos(prev => [...prev, uploadData]);
    setUploadSuccess(`Video uploaded successfully: ${uploadData?.fileName}`);
    setUploadError('');
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setUploadSuccess('');
    }, 3000);
  };

  const handleVideoUploadError = (error) => {
    setUploadError(error);
    setUploadSuccess('');
  };

  const handlePhraseChange = (phraseId, field, value) => {
    const updatedPhrases = data?.phrases?.map(phrase => {
      if (phrase?.id === phraseId) {
        return {
          ...phrase,
          [field]: value
        };
      }
      return phrase;
    });

    onChange({
      ...data,
      phrases: updatedPhrases
    });
  };

  const addNewPhrase = () => {
    const newId = Math?.max(...(data?.phrases?.map(p => p?.id) || [0])) + 1;
    const newPhrase = {
      id: newId,
      phrase: '',
      emotion: '',
      acting: '',
      vocal: ''
    };

    onChange({
      ...data,
      phrases: [...(data?.phrases || []), newPhrase]
    });
  };

  const removePhrase = (phraseId) => {
    const updatedPhrases = data?.phrases?.filter(phrase => phrase?.id !== phraseId);
    onChange({
      ...data,
      phrases: updatedPhrases
    });
  };

  return (
    <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
      {/* Section Header with Video Upload */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-2xl font-heading font-bold text-white">Level 5: Mastering</h2>
        
        <VideoUploadButton
          level="level5"
          subsection="phrases"
          songId={songId}
          talentId={talentId}
          onUploadSuccess={handleVideoUploadSuccess}
          onUploadError={handleVideoUploadError}
          size="md"
          className="shrink-0"
        />
      </div>

      {/* Upload Messages */}
      {uploadError && (
        <div className="mb-4 bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-3">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-red-800" />
            <p className="text-red-800 text-sm">{uploadError}</p>
          </div>
        </div>
      )}

      {uploadSuccess && (
        <div className="mb-4 bg-green-50 bg-opacity-90 border border-green-200 rounded-xl p-3">
          <div className="flex items-center space-x-2">
            <Icon name="CheckCircle" size={16} className="text-green-800" />
            <p className="text-green-800 text-sm">{uploadSuccess}</p>
          </div>
        </div>
      )}

      {/* Uploaded Videos List */}
      {uploadedVideos?.length > 0 && (
        <div className="mb-6 bg-blue-50 bg-opacity-90 border border-blue-200 rounded-xl p-4">
          <h3 className="text-blue-800 font-medium mb-2">Uploaded Videos ({uploadedVideos?.length})</h3>
          <div className="space-y-2">
            {uploadedVideos?.map((video, index) => (
              <div key={index} className="flex items-center justify-between text-sm text-blue-700 bg-white bg-opacity-50 rounded-lg p-2">
                <div className="flex items-center space-x-2">
                  <Icon name="Video" size={14} />
                  <span>{video?.fileName}</span>
                </div>
                <span className="text-xs">
                  {(video?.fileSize / (1024 * 1024))?.toFixed(1)} MB
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phrases Section */}
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-white">Phrase Analysis</h3>
          <button
            type="button"
            onClick={addNewPhrase}
            className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white border border-white border-opacity-30 rounded-lg transition-all duration-200 flex items-center space-x-2"
          >
            <Icon name="Plus" size={16} />
            <span>Add Phrase</span>
          </button>
        </div>

        {data?.phrases?.map((phrase) => (
          <div key={phrase?.id} className="border border-white border-opacity-20 rounded-xl p-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-md font-semibold text-white">Phrase {phrase?.id}</h4>
              {data?.phrases?.length > 1 && (
                <button
                  type="button"
                  onClick={() => removePhrase(phrase?.id)}
                  className="text-red-300 hover:text-red-100 p-1 rounded-lg hover:bg-red-500 hover:bg-opacity-20"
                >
                  <Icon name="Trash2" size={16} />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Phrase Text */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Phrase
                </label>
                <input
                  type="text"
                  value={phrase?.phrase || ''}
                  onChange={(e) => handlePhraseChange(phrase?.id, 'phrase', e?.target?.value)}
                  placeholder="Enter the specific phrase or lyric line"
                  className="w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                />
              </div>

              {/* Emotion */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Emotion
                </label>
                <textarea
                  value={phrase?.emotion || ''}
                  onChange={(e) => handlePhraseChange(phrase?.id, 'emotion', e?.target?.value)}
                  placeholder="Describe the emotion for this phrase"
                  rows={3}
                  className="w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
                />
              </div>

              {/* Acting */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Acting
                </label>
                <textarea
                  value={phrase?.acting || ''}
                  onChange={(e) => handlePhraseChange(phrase?.id, 'acting', e?.target?.value)}
                  placeholder="Describe the acting approach for this phrase"
                  rows={3}
                  className="w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
                />
              </div>

              {/* Vocal */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Vocal
                </label>
                <textarea
                  value={phrase?.vocal || ''}
                  onChange={(e) => handlePhraseChange(phrase?.id, 'vocal', e?.target?.value)}
                  placeholder="Describe the vocal technique and delivery for this phrase"
                  rows={3}
                  className="w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
                />
              </div>
            </div>
          </div>
        ))}

        {data?.phrases?.length === 0 && (
          <div className="text-center py-8">
            <p className="text-blue-200 mb-4">No phrases added yet</p>
            <button
              type="button"
              onClick={addNewPhrase}
              className="px-6 py-3 bg-white bg-opacity-20 hover:bg-opacity-30 text-white border border-white border-opacity-30 rounded-lg transition-all duration-200 flex items-center space-x-2 mx-auto"
            >
              <Icon name="Plus" size={18} />
              <span>Add First Phrase</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LevelFiveSection;